import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../../assets/styles/AuthComponents.module.scss";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import AuthService from "../../../api/services/auth.service";
import { setCookie } from "../../../utils/cookies";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import Input from "../../../components/common/input/Input";
import AuthHeader from "../../../components/common/header/auth-header/AuthHeader";
import Button from "../../../components/common/button/Button";

const Login = () => {
  const { t } = useTranslation("translation");
  const { setEmail } = useAuth();
  const [emailValue, setEmailValue] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessages, setErrorMessages] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Backend error message map
  const mapErrorMessage = (error: string): string => {
    const errorMap: Record<string, string> = {
      "Invalid credentials": t("login.errors.invalid_credentials") as string,
      "User Not Active": t("login.errors.user_not_active") as string,
    };
    return errorMap[error] || error;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: typeof errorMessages = {};

    if (!emailValue) errors.email = t("login.errors.email_required") as string;

    if (Object.keys(errors).length > 0) {
      setErrorMessages(errors);
      return;
    }
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("email", emailValue);
      formData.append("password", password);
      setEmail(emailValue);
      const res = await AuthService.login(formData);
      setCookie("LuxMartAdminDashboardAccessToken", res.data.access_token);
      navigate("/dashboard");
    } catch (error: any) {
      setLoading(false);

      const backendError = error?.response?.data?.error;

      if (backendError) {
        setErrorMessage(mapErrorMessage(backendError));
      } else {
        setErrorMessage(t("login.errors.unexpected_error") as string);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <TitleUpdater title={t("login.title") as string} />
      <form className={styles.form} onSubmit={handleSubmit}>
        <AuthHeader title={t("login.title") as string} />
        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
        <Input
          type="email"
          id="email"
          placeholder={t("login.email_placeholder") as string}
          value={emailValue}
          onChange={(e) => {
            setEmailValue(e.target.value);
            setErrorMessage("");
            setErrorMessages((prev) => ({ ...prev, email: "" }));
          }}
          label={t("login.email_label") as string}
          error={errorMessages.email}
        />
        <Input
          type="password"
          id="password"
          placeholder={t("login.password_placeholder") as string}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setErrorMessage("");
            setErrorMessages((prev) => ({ ...prev, password: "" }));
          }}
          label={t("login.password_label") as string}
        />
        <Button type="submit" className={styles.button} disabled={loading}>
          {loading ? t("login.loading") : t("login.button")}
        </Button>
      </form>
    </div>
  );
};

export default Login;
